import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type ActivityLog from 'lumen/models/activity-log';
import type { Transition } from 'lumen/services/transition';
import {
  ACTION_STATUSES,
  type ActionStatus,
  ACTIVITY_CATEGORY_TRANSLATION_KEYS,
  type ActivityCategory,
} from 'lumen/utils/constants/activity-action-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

const VALID_CATEGORIES = Object.keys(
  ACTIVITY_CATEGORY_TRANSLATION_KEYS,
) as ActivityCategory[];

function isValidCategory(value: string | undefined): value is ActivityCategory {
  return !!value && VALID_CATEGORIES.includes(value as ActivityCategory);
}

function isValidActionStatus(value: string | undefined): value is ActionStatus {
  return !!value && (ACTION_STATUSES as readonly string[]).includes(value);
}

interface RouteParams {
  actionStatus?: string;
  category?: string;
  endDate?: string | null;
  page?: number;
  pageSize?: number;
  searchKeys?: string[];
  startDate?: string | null;
  userIds?: string[];
}

interface ActivityLogMeta {
  available_categories: ActivityCategory[];
  count: number;
  page: number;
  page_count: number;
  page_size: number;
  user_list: {
    email: string;
    name: string;
    user_id: number;
  }[];
}

export interface ActivityLogData {
  activityLogs: ActivityLog[];
  meta: ActivityLogMeta;
}

export interface ActivityModel {
  activityLogsTask: typeof HotelSettingsActivityRoute.prototype.loadActivityLogsTask;
}

export default class HotelSettingsActivityRoute extends Route {
  queryParams = {
    actionStatus: { refreshModel: true },
    category: { refreshModel: true },
    startDate: { refreshModel: true },
    endDate: { refreshModel: true },
    page: { refreshModel: true },
    pageSize: { refreshModel: true },
    searchKeys: { refreshModel: true },
    userIds: { refreshModel: true },
  };
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare navigation: Services['navigation'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  loadActivityLogsTask = task(
    { restartable: true },
    async (params: RouteParams) => {
      try {
        const response = await this.store.query<ActivityLog>('activity-log', {
          namespace: APIGATEWAY,
          typeKey: 'audit/activity-history',
          action_status: params.actionStatus ?? undefined,
          category: params.category,
          page: params.page ?? 1,
          page_size: params.pageSize ?? 100,
          search_keys: params.searchKeys?.length
            ? params.searchKeys
            : undefined,
          start_date: params.startDate,
          end_date: params.endDate,
          user_ids: params.userIds,
          subscription_id: this.hotelAndMarketService.hotel?.id,
        });

        return {
          activityLogs: [...response],
          meta: response.meta as unknown as ActivityLogMeta,
        } satisfies ActivityLogData;
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );

  beforeModel(transition: Transition): void {
    if (this.navigation.redirectToAccountSelection(transition)) {
      return;
    }

    const params = this.paramsFor(this.routeName) as RouteParams;
    const { actionStatus, category } = params;

    const sanitizedParams = { ...params };

    if (!isValidCategory(category)) {
      sanitizedParams.category = 'account';
    }

    if (actionStatus !== undefined && !isValidActionStatus(actionStatus)) {
      sanitizedParams.actionStatus = undefined;
    }

    if (
      sanitizedParams.category !== category ||
      sanitizedParams.actionStatus !== actionStatus
    ) {
      this.transition.validateQueryParams(
        transition,
        params,
        sanitizedParams,
        this.routeName,
      );
    }
  }

  model(params: RouteParams): ActivityModel {
    const startDate =
      params.startDate ??
      this.hotelAndMarketService
        .getLocalTime()
        .subtract(1, 'month')
        .format('YYYY-MM-DD');
    const endDate =
      params.endDate ??
      this.hotelAndMarketService.getLocalTime().format('YYYY-MM-DD');
    void this.loadActivityLogsTask.perform({
      ...params,
      startDate,
      endDate,
    });

    return {
      activityLogsTask: this.loadActivityLogsTask,
    };
  }
}
